import React, { useState } from 'react'
import { PaymentElement } from '@stripe/react-stripe-js'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { useSettings } from '../../../../hooks/useSettings'
import { getPrimaryClass, getHoverClass } from '../../../../utils/themeUtils'

const PaymentForm = ({ setActiveMenu }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { currentTheme } = useSettings()
  const { t } = useTranslation('payments')

  const [message, setMessage] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false)
  const [isHttps, setIsHttps] = useState(true)

  // Check if using HTTPS
  React.useEffect(() => {
    if (window.location.protocol !== 'https:') {
      setIsHttps(false)
      setMessage(t('rechargingStation.paymentForm.httpsMessage'))
    }
  }, [t])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setIsProcessing(true)

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          // Make sure to change this to your payment completion page
          return_url: `${window.location.origin}/payment-completion`,
        },
      })

      if (error) {
        if (error.type === 'card_error' || error.type === 'validation_error') {
          setMessage(error.message)
        } else {
          setMessage(t('rechargingStation.paymentForm.unexpectedError'))
        }
      } else if (paymentIntent) {
        switch (paymentIntent.status) {
          case 'succeeded':
            setMessage(t('rechargingStation.paymentForm.paymentSuccessful'))
            break
          case 'processing':
            setMessage(t('rechargingStation.paymentForm.paymentProcessing'))
            break
          case 'requires_payment_method':
            setMessage(t('rechargingStation.paymentForm.paymentFailedRetry'))
            break
          default:
            setMessage(t('rechargingStation.paymentForm.unexpectedStatus'))
        }
      }
    } catch (error) {
      setMessage(t('rechargingStation.paymentForm.confirmationFailed'))
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form
      className="flex flex-col w-full h-full"
      id="payment-form"
      onSubmit={handleSubmit}
    >
      <div className="flex-1">
        {!isHttps ? (
          <div className="p-6 rounded-lg bg-red/10 border-2 border-red text-red">
            <h3 className="font-bold mb-2">
              {t('rechargingStation.paymentForm.httpsRequired')}
            </h3>
            <p>{t('rechargingStation.paymentForm.httpsMessage')}</p>
          </div>
        ) : (
          <PaymentElement
            id="payment-element"
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
            }}
            onReady={() => {
              setIsPaymentElementReady(true)
            }}
            onLoadError={() => {
              setMessage(t('rechargingStation.paymentForm.loadError'))
            }}
          />
        )}
      </div>
      {/* Show any error or success messages */}
      {message && (
        <div
          className={clsx('font-light mt-4 p-3 rounded-lg', {
            'bg-red/10 text-red':
              message.includes('failed') || message.includes('error'),
            'bg-green/10 text-green': message.includes('successful'),
          })}
        >
          {message}
        </div>
      )}
      {!stripe || !elements || !isPaymentElementReady ? (
        <div className="mt-4 text-sm text-center opacity-70">
          {!isHttps
            ? ''
            : !stripe || !elements
              ? t('payment.loadingOptions')
              : t('rechargingStation.paymentForm.preparingForm')}
        </div>
      ) : null}
      <button
        disabled={
          !isHttps ||
          isProcessing ||
          !stripe ||
          !elements ||
          !isPaymentElementReady
        }
        className={clsx(
          'w-full h-[40px] text-white rounded-xl transition duration-100 active:shadow-[inset_0px_0px_14px_5px_#00000020] flex justify-center items-center',
          getPrimaryClass(currentTheme),
          {
            'mt-4': !message,
            'mt-3': message,
            [getHoverClass(currentTheme)]:
              isHttps &&
              !isProcessing &&
              stripe &&
              elements &&
              isPaymentElementReady,
            'opacity-50 cursor-not-allowed':
              !isHttps ||
              isProcessing ||
              !stripe ||
              !elements ||
              !isPaymentElementReady,
          }
        )}
      >
        {isProcessing ? (
          <img
            className="h-[40px]"
            src="/assets/images/button-loader.svg"
            alt=""
          />
        ) : (
          <p>{t('rechargingStation.paymentForm.payNow')}</p>
        )}
      </button>
    </form>
  )
}

export default PaymentForm
